import * as React from 'react';
import { TabList, Tab, TabValue, tokens, makeStyles, Text } from '@fluentui/react-components';
import { useNavigation } from '../../hooks/routing';
import { useClient } from '../../hooks/client/ClientContext';
import { MainScreenSkeleton } from './MainScreenSkeleton';
import { EventTab, StakeholdersTab } from './tabs';
import { useItem } from '../../hooks/item/ItemProvider';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorCircleFilled } from '@fluentui/react-icons';
import AttachmentsTab from './tabs/AttachmentsTab';
import { Step, TeachingPopover } from '../../components/TeachingPopover';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const useStyles = makeStyles({
  tab: {
    padding: 0, // Required to override inline/block paddings
    paddingInline: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalM,
    flexShrink: 1,
    minWidth: 0,
    '& .fui-Tab__content': {
      alignItems: 'center',
      display: 'flex',
      gap: tokens.spacingHorizontalXS,
    },
  },
  tabLabel: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    minWidth: 0,
  },
  content: {
    overflowY: 'auto',
  },
});

export const MainScreen = () => {
  const { params, setCurrentParams } = useNavigation<'main'>();
  const [selectedValue, setSelectedValue] = React.useState<TabValue>(params.selectedTab ?? 'event');
  const { newStakeholders, attachments, getIncludedStakeholders, emailObject } = useItem();
  const { configuration } = useClient();
  const { t } = useTranslation();

  const [showTutorial, setShowTutorial] = useLocalStorage('showTutorial', true);

  const eventTabRef = React.useRef(null);
  const attachmentsTabRef = React.useRef(null);
  const stakeholderssTabRef = React.useRef(null);

  const styles = useStyles();

  if (!configuration) {
    return <MainScreenSkeleton />;
  }

  const steps: Step[] = [
    {
      title: configuration.entityDefinitions.content.name,
      content: t('EVENT_TAB.TEACHING_DESCRIPTION', { eventName: configuration.entityDefinitions.content.name }),
      targetRef: eventTabRef,
    },
    {
      title: t('COMMON.ATTACHMENTS'),
      content: t('ATTACHMENTS_TAB.TEACHING_DESCRIPTION', { documentName: configuration.entityDefinitions.attachment.name }),
      targetRef: attachmentsTabRef,
    },
    {
      title: configuration.entityDefinitions.stakeholder.pluralName,
      content: t('STAKEHOLDERS_TAB.TEACHING_DESCRIPTION', { stakeholderPluralName: configuration.entityDefinitions.stakeholder.pluralName }),
      targetRef: stakeholderssTabRef,
    },
  ];

  return (
    <>
      <TabList
        style={{
          backgroundColor: tokens.colorNeutralStrokeOnBrand,
        }}
        selectedValue={selectedValue}
        onTabSelect={(_, data) => {
          setSelectedValue(data.value);
          setCurrentParams({ selectedTab: data.value as string });
        }}
      >
        <Tab
          className={styles.tab}
          value="event"
          ref={eventTabRef}
          title={configuration.entityDefinitions.content.name}
        >
          {configuration.entityDefinitions.content.name}
        </Tab>
        <Tab
          value="attachments"
          ref={attachmentsTabRef}
          className={styles.tab}
          title={t('COMMON.ATTACHMENTS')}
        >
          <Trans
            i18nKey={'MAIN_SCREEN.ATTACHMENTS'}
            values={{
              attachmentsCount: attachments.filter((a) => !!emailObject.selectedAttachments.get(a.id)).length,
            }}
            components={[
              <Text
                className={styles.tabLabel}
                key={0}
              />,
              <Text key={1} />,
            ]}
          />
        </Tab>
        <Tab
          value="stakeholders"
          className={styles.tab}
          ref={stakeholderssTabRef}
          title={configuration.entityDefinitions.stakeholder.pluralName}
        >
          <Trans
            i18nKey={'MAIN_SCREEN.STAKEHOLDERS'}
            values={{
              stakeholderPluralName: configuration.entityDefinitions.stakeholder.pluralName,
              stakeholderCount: getIncludedStakeholders().length,
            }}
            components={[
              <Text
                className={styles.tabLabel}
                key={0}
              />,
              <Text key={1} />,
            ]}
          />
          {newStakeholders.length > 0 && (
            <ErrorCircleFilled
              style={{ flexShrink: 0 }}
              fontSize={tokens.fontSizeBase400}
              color={tokens.colorPaletteMarigoldBackground3}
            />
          )}
        </Tab>
      </TabList>
      <div className={styles.content}>
        <div>{selectedValue === 'event' && <EventTab />}</div>
        <div>{selectedValue === 'attachments' && <AttachmentsTab />}</div>
        <div>{selectedValue === 'stakeholders' && <StakeholdersTab />}</div>
      </div>
      <TeachingPopover
        open={showTutorial}
        onComplete={() => setShowTutorial(false)}
        onDismiss={() => setShowTutorial(false)}
        steps={steps}
      ></TeachingPopover>
    </>
  );
};
